// Pure helpers for chat real-time behaviour: idempotent send retries, stream reconnect backoff and
// typing-indicator state. Kept free of React so they can be unit tested.

export function makeClientNonce() {
  try {
    if (typeof crypto !== 'undefined' && crypto && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID().replace(/-/g, '');
    }
  } catch {}
  let s = '';
  for (let i = 0; i < 4; i++) s += Math.random().toString(36).slice(2, 10);
  return s.slice(0, 32).padEnd(16, '0');
}

// Network failures (no status) and server/gateway errors may succeed on retry; 4xx answers will not.
export const SEND_RETRY_DELAYS_MS = [500, 1500, 4000];
const MAX_RETRY_WAIT_MS = SEND_RETRY_DELAYS_MS[SEND_RETRY_DELAYS_MS.length - 1];

// The server's suggested wait (429 responses carry retry_after_ms), or 0.
function retryAfterMs(err) {
  const ms = Number(err && err.data && err.data.retry_after_ms);
  return Number.isFinite(ms) && ms > 0 ? ms : 0;
}

// 429 covers two cases: a transient rate limit (retry after the server's wait) and a policy refusal such as
// slowmode or a safety rule (code other than rate_limited, or a wait longer than the retry budget), which must
// not be resent automatically.
export function isRetryableSendError(err) {
  const status = Number(err && err.status);
  if (!status) return true;
  if (status === 429) {
    const code = err && err.data && err.data.code;
    return (!code || code === 'rate_limited') && retryAfterMs(err) <= MAX_RETRY_WAIT_MS;
  }
  return status === 408 || status >= 500;
}

// Runs attempt() until it resolves, retrying retryable failures. The caller reuses one client nonce
// for every attempt, so the server stores the message at most once.
export async function sendWithRetry(attempt, { delays = SEND_RETRY_DELAYS_MS, sleep, shouldRetry = isRetryableSendError } = {}) {
  const wait = sleep || ((ms) => new Promise((r) => setTimeout(r, ms)));
  let lastErr = null;
  for (let i = 0; i <= delays.length; i++) {
    try {
      return await attempt(i);
    } catch (e) {
      lastErr = e;
      if (i === delays.length || !shouldRetry(e)) break;
      await wait(Math.max(delays[i], retryAfterMs(e)));
    }
  }
  throw lastErr;
}

export function describeSendError(err) {
  const status = Number(err && err.status);
  if (!status) return 'Message not sent: connection problem. Your text is kept; press Enter to try again.';
  if (status === 429) return (err && err.data && err.data.error) || 'Message rate limit reached. Wait a moment and try again.';
  if (status >= 500) return 'Message not sent: the server had a problem. Your text is kept; try again.';
  // Permission, timeout, lock and safety refusals carry a readable reason and a code.
  const data = (err && err.data) || {};
  if (data.error) return data.code === 'timed_out' ? `${data.error}. You can appeal in Settings → Moderation & appeals.` : String(data.error);
  return (err && err.message) || 'Message not sent.';
}

// Exponential backoff with jitter for manual stream reconnects: 1s, 2s, 4s ... capped at 30s.
export function reconnectDelayMs(attempt, random = Math.random) {
  const base = Math.min(30000, 1000 * Math.pow(2, Math.max(0, attempt)));
  return Math.round(base * (0.75 + random() * 0.5));
}

// ---- typing indicators ----
// state: { [userId]: { userId, username, expiresAt } }

export function applyTypingEvent(state, ev, now = Date.now(), myUserId = 0) {
  const data = ev || {};
  const uid = Number(data.user_id) || 0;
  if (!uid || uid === Number(myUserId)) return state;
  const next = { ...(state || {}) };
  if (data.stopped || !(Number(data.expires_in_ms) > 0)) {
    delete next[uid];
    return next;
  }
  next[uid] = {
    userId: uid,
    username: String(data.username || `User ${uid}`),
    expiresAt: now + Math.min(15000, Number(data.expires_in_ms)),
  };
  return next;
}

export function clearTypingFor(state, userId) {
  const uid = Number(userId) || 0;
  if (!uid || !state || !state[uid]) return state;
  const next = { ...state };
  delete next[uid];
  return next;
}

export function pruneTyping(state, now = Date.now()) {
  const entries = Object.entries(state || {});
  const kept = entries.filter(([, v]) => v && v.expiresAt > now);
  if (kept.length === entries.length) return state;
  return Object.fromEntries(kept);
}

export function typingLabel(state) {
  const names = Object.values(state || {})
    .sort((a, b) => a.username.localeCompare(b.username))
    .map((v) => v.username);
  if (!names.length) return '';
  if (names.length === 1) return `${names[0]} is typing…`;
  if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`;
  if (names.length === 3) return `${names[0]}, ${names[1]} and ${names[2]} are typing…`;
  return 'Several people are typing…';
}

// Client-side throttle for outgoing typing pings (server also throttles).
export function createTypingNotifier(post, { minGapMs = 3000, now = () => Date.now() } = {}) {
  let last = -Infinity;
  let active = false;
  return {
    typing() {
      const t = now();
      if (t - last < minGapMs) return false;
      last = t;
      active = true;
      Promise.resolve(post({})).catch(() => {});
      return true;
    },
    stopped() {
      if (!active) return false;
      active = false;
      last = -Infinity;
      Promise.resolve(post({ stopped: true })).catch(() => {});
      return true;
    },
  };
}
